import React, { useState } from "react";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import AddIcon from "../../assets/icons/Icon_Library/application icons/assets/events/add_circle-01.svg2";
import RemoveIcon from "../../assets/icons/Icon_Library/application icons/assets/events/remove-01.svg2";
import { Button, Form, Tab, Table } from "react-bootstrap";
import {
  useAddProjectToSolutionMutation,
  useGetProjectInterfacesQuery,
  useGetSolutionByUuidQuery,
  useListProjectQuery,
  useRemoveProjectFromSolutionMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

export const solutionRolesMapping = {
  generic: { label: "Generic", interface: [] },
  dependency: { label: "Dependency", interface: [] },
  "event-create": { label: "Event Create", interface: ["id"], for: "events" },
  "add-ticket": { label: "Add Ticket", interface: ["id"], for: "events" },
  "create-update": {
    label: "Create an Update",
    interface: ["id", "message"],
    for: "events",
  },
  "update-event": {
    label: "Update Event",
    interface: ["id", "name", "description", "severity"],
    for: "events",
  },
  "create-communication": {
    label: "Create Communication",
    interface: ["id", "message"],
    for: "events",
  },
  "context-project": {
    label: "Context Project",
    interface: ["id"],
    for: "events",
  },
  "close-event": { label: "Close Event", interface: ["id"], for: "events" },
  "inbound-message": {
    label: "Inbound Message",
    interface: ["from", "message"],
  },
  "inbound-api": { label: "Inbound API", interface: [] },
  "ping-monitor": {
    label: "Ping Monitor",
    interface: ["url", "status", "response_code"],
  },
  "weather-monitor": {
    label: "Weather Monitor",
    interface: ["id", "short_message", "long_message"],
  },
};

export default function SelectSolutionProject({ solution: _solution }) {
  const [selectedProject, setSelectedProject] = React.useState("");
  const [addProject] = useAddProjectToSolutionMutation();
  const [removeProject] = useRemoveProjectFromSolutionMutation();
  const [description, setDescription] = useState("");
  const [role, setRole] = useState("generic");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);

  const projects =
    useGetProjectInterfacesQuery({
      for: "solutions",
      role: solutionRolesMapping[role].interface,
      limit: 100,
    }).data?.projects || [];
  const solution = useGetSolutionByUuidQuery({ uuid: _solution.Uuid }).data;

  const addProjectToSolution = async (componentUuid, p_description, p_role) => {
    const res = await addProject({
      ProjectUuid: componentUuid,
      SolutionUuid: solution.Uuid,
      Description: p_description,
      Role: p_role,
    });

    if ("data" in res) {
      const sol = res.data.solution;
      const currentComponent = sol.Components.find(
        (c) => c.ItemUuid === componentUuid
      );
      toast.success(
        `Project "${currentComponent?.Item?.Label}" added to solution successfully`
      );
      setDescription("");
      setSelectedProject("");
      if (sol?.Components) {
        sol.Components.forEach(async (component) => {
          if ("Component" in component.Bag.External) {
            Object.entries(component.Bag.External.Component).forEach(
              async ([componentKey, componentUuid]) => {
                if (sol.Components.some((c) => c.ItemUuid === componentUuid)) {
                  return;
                }
                await addProjectToSolution(
                  componentUuid,
                  "Component dependency",
                  "dependency"
                );
              }
            );
          }
        });
      }
    }

    if ("error" in res && res.error?.data?.errors) {
      toast.error(
        "Error adding project to solution: " +
          JSON.stringify(res.error.data.errors)
      );
    }
  };

  return (
    <>
      <div className="">
        <div className=" me-3">
          <Form.Label className="fw-bold">Role: </Form.Label>
          <Form.Select
            className="mb-3"
            aria-label="status-select"
            onChange={(e) => {
              setRole(e.target.value);
            }}
            value={role}
          >
            {Object.entries(solutionRolesMapping)
              .filter(([_, { for: kind }]) => {
                if (kind) {
                  return kind === _solution.Kind;
                }
                return true;
              })
              .map(([value, { label, interface: interfaceVars }]) => (
                <option key={value} value={value}>
                  {label}{" "}
                  {interfaceVars.length > 0 &&
                    `(interface: ${interfaceVars.join(", ")})`}
                </option>
              ))}
          </Form.Select>
        </div>

        <div className=" me-3">
          <div className="fw-bold my-1">Select a Component:</div>
          <Input
            inputType="select"
            value={[selectedProject]}
            onChange={([value]) => setSelectedProject(value)}
            options={projects?.map((project) => ({
              label: project.Label,
              value: project.UUID,
            }))}
          />
        </div>
        <div className="text-secondary mt-2">
          Components must be set as "solution" and "production" in the component
          configuration.Components must also match correct variable interface
          for each role.{" "}
        </div>
        <div className="d-flex align-items-end mt-3">
          <div className="me-3 flex-grow-1">
            <div className="fw-bold my-1">Role Description:</div>
            <Input
              inputType="textArea"
              placeholder="Role Description"
              value={[description]}
              onChange={([value]) => setDescription(value)}
            />
          </div>
          <div className=" ms-3">
            <Button
              disabled={
                !selectedProject ||
                !description ||
                solution.Status === "public" ||
                adding
              }
              className=""
              variant=""
              onClick={async () => {
                setAdding(true);

                await addProjectToSolution(selectedProject, description, role);

                setTimeout(() => {
                  setAdding(false);
                }, 700);
              }}
            >
              <AddIcon width={15} className="me-2" />
              Add the component
              {adding && (
                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
              )}
            </Button>
          </div>
        </div>
      </div>
      {solution?.Components && (
        <div className="mt-3">
          <h5 className="fw-bold my-3">Current Components in Solution:</h5>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Label</th>
                <th>Depends on</th>
                <th>Description</th>
                <th>Status</th>
                <th>Version</th>
                <th>Role</th>
                <th>Role Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {solution.Components?.map((component) => (
                <tr key={component.Item.UUID}>
                  <td>{component.Item.Label}</td>
                  <td>
                    {component.Bag.External?.Component
                      ? Object.values(component.Bag.External.Component)
                          .map(
                            (uuid) =>
                              solution.Components?.find(
                                (c) => c.ItemUuid === uuid
                              )?.Item.Label || "Unknown"
                          )
                          .join(", ")
                      : "-"}
                  </td>
                  <td>{component.Item.Description}</td>
                  <td>{component.Item.Status}</td>
                  <td>{component.Item.Version}</td>
                  <td>{component.Role}</td>
                  <td>{component.Description}</td>
                  <td>
                    <Button
                      className="text-danger"
                      variant=""
                      type="button"
                      disabled={
                        solution.Components?.some((c) =>
                          Object.values(c.Bag.External?.Component || {}).some(
                            (uuid) => uuid === component.ItemUuid
                          )
                        ) ||
                        solution.Status === "public" ||
                        removing
                      }
                      onClick={() => {
                        setRemoving(true);
                        removeProject({
                          ComponentUuid: component.ComponentUuid,

                          SolutionUuid: solution.Uuid,
                        }).then((res) => {
                          if ("data" in res) {
                            toast.success(
                              "Project removed from solution successfully"
                            );
                          }
                          if ("error" in res && res.error?.data?.errors) {
                            toast.error(
                              `Error removing project from solution: ${JSON.stringify(
                                res.error.data.errors
                              )}`
                            );
                          }
                          setTimeout(() => {
                            setRemoving(false);
                          }, 700);
                        });
                      }}
                    >
                      <RemoveIcon width={15} className="me-2" />
                      Remove
                      {removing && (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="ms-2"
                        />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}
